import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Collection,
  Inventory,
  PatientSubcollection,
  TScores
} from 'lib/documents';
import { getTimestampedDocId } from 'lib/utils/object';
import {
  handleError,
  handlePending,
  PageState,
  ThunkApi
} from 'lib/utils/redux';
import moment, { Moment } from 'moment';

import { createRepo } from 'lib/api/firebase';

import { tScoreMapCategory, tScoreMapping } from 'lib/documents/promisTScores';

import {
  QuestionnaireConfig,
  QuestionnaireSubsectionConfig
} from 'data/questionnaire-config-type';
import { QuestionConfigWithResponse } from 'features/patient/assessment/_components/questionnaire-page';
import { savePendingInventory } from 'features/patient/assessment/assessment-controller/questionnaire-control-flow-slice';
import { PromisQuestionnaire } from 'features/patient/inventory-analysis/utils/questionnaires';
import { PostExecutionCallbacks } from '../../_components/questionnaire-paginator-types';
import { controlflowSubmissionEventHandler } from '../../assessment-controller/_utils/control-flow-submission-eventhandler';
import { categories, tScoreCategories } from './promisTypes';
const promisData: QuestionnaireConfig = PromisQuestionnaire;

export interface PromisInventory extends Inventory {
  data: Record<keyof typeof tScoreMapping, number> &
    Record<ValueOf<typeof tScoreMapping>, number> &
    Record<string, number>;
}

export interface PromisState extends PageState {
  promis: PromisInventory;
  invalidItems: string[];
  lastUpdated?: Moment;
  showModal: boolean;
  isSubmitting: boolean;
  /**
   * A list of all promis section configurations from promis.json
   *
   * **readonly** This value is never modified during runtime
   */
  promisBySection: QuestionnaireSubsectionConfig[];
}

export const NOT_SELECTED = -1;
const PROMIS_SUBSECTION_NAMES: string[] = Object.keys(promisData.subsections);

export function createEmptyPromisInventory(): PromisInventory {
  // gather all questions just for submission and value entry
  const questions: Record<string, number> = {};
  PROMIS_SUBSECTION_NAMES.forEach(section => {
    promisData.subsections[`${section}`]['questions'].reduce((acc, q) => {
      acc[q.title] = NOT_SELECTED;
      return acc;
    }, questions);
  });

  Object.keys(tScoreMapping).forEach(
    calculatedSection => (questions[calculatedSection] = null as any)
  );

  return {
    id: 'unknown',
    date: moment(),
    data: questions as any
  };
}

export const PROMIS_SECTIONS = createPromisBySection();

/**
 * Creates a list of all Promis subsections
 * @returns
 */
export function createPromisBySection(): QuestionnaireSubsectionConfig[] {
  // Get each subsection in each section
  return PROMIS_SUBSECTION_NAMES.map(
    section => promisData.subsections[section]
  );
}

const initialLoad = createAsyncThunk('promis/initialLoad', async () => {
  return {
    date: undefined
  };
});

const submit = createAsyncThunk<
  boolean,
  PostExecutionCallbacks,
  ThunkApi<PromisState>
>('promis/submit', async (callbacks, { getState, dispatch }) => {
  return await controlflowSubmissionEventHandler(callbacks, async () => {
    dispatch(promisSlice.actions.validate());

    const { invalidItems } = getState();
    if (invalidItems.length) {
      console.error(
        'Submission was stopped because there are still invalid items present',
        invalidItems
      );
      return false;
    }

    /**
     * WARNING: Possible race condition
     */
    dispatch(promisSlice.actions.setPromisDate());
    dispatch(promisSlice.actions.calculateCategories());
    dispatch(promisSlice.actions.calculateTScores());

    const { promis } = getState();
    await savePendingInventory(PatientSubcollection.InventoriesPromis, promis);
    return true;
  });
});

export const initialState: PromisState = {
  hasError: false,
  promis: createEmptyPromisInventory(), // should this be the current page's questions? or the total amount of questions or should the filter be on the react component?
  invalidItems: [],
  isLoading: false,
  isSubmitting: false,
  showModal: false,
  promisBySection: createPromisBySection()
};

const promisSlice = createSlice({
  name: 'promis',
  initialState,
  reducers: {
    setPromisDate: state => {
      const now = moment();
      state.promis.id = getTimestampedDocId(now);
      state.promis.date = now;
    },
    closeModal: state => {
      state.showModal = false;
    },
    updatePromisItem: (
      state,
      {
        payload
      }: PayloadAction<Pick<QuestionConfigWithResponse, 'title' | 'value'>>
    ) => {
      const { title, value } = payload;
      state.invalidItems = state.invalidItems.filter(name => name != title);
      state.promis.data[title] = Number(value);
    },
    validate: state => {
      state.invalidItems = Object.entries(state.promis.data)
        .filter(([, value]) => value === NOT_SELECTED)
        .map(([key]) => key);
    },
    /**
     * Calculate the scores for each category from the filled questions
     */
    calculateCategories: state => {
      // we need mapping object from section => actual saved "value"
      // we calculate individual section totals and have another function that calculates the tscore interpretations
      // set category totals on state.promis.data to 0
      for (const category of categories) {
        state.promis.data[category] = 0;
      }

      // this is kind of ugly
      // For each section
      state.promisBySection.forEach(section => {
        // For each question in the section
        section.questions.forEach(question => {
          const value = state.promis.data[question.title];
          // For each category in the question
          question.categories.forEach((category: string) => {
            // Aggregate the question value into promis.data[questionCategory]
            state.promis.data[category] += value;
          });
        });
      });
    },
    calculateTScores: state => {
      // For each t-score category
      for (const category of tScoreCategories) {
        // Get the raw score
        const rawScore = state.promis.data[category];

        // Transform the category key to the t-score key to use when saving
        const mapping = tScoreMapping[category];

        // Get the t-score data transformer to use
        const tCategory = tScoreMapCategory[category];

        // Apply the transformation
        const tScore = TScores.findTScore(rawScore, tCategory);

        // Save the change
        state.promis.data[mapping] = tScore;
      }
    }
  },
  extraReducers: builder => {
    builder.addCase(initialLoad.pending, handlePending);
    builder.addCase(initialLoad.rejected, handleError);
    builder.addCase(initialLoad.fulfilled, (state, { payload }) => {
      // The data has finished loading
      state.isLoading = false;
      state.lastUpdated = payload ? payload.date : undefined;
    });

    builder.addCase(submit.pending, state => {
      state.isSubmitting = true;
    });
    builder.addCase(submit.rejected, handleError);
    builder.addCase(submit.fulfilled, (state, { payload: isSaved }) => {
      state.isSubmitting = false;
      if (isSaved) {
        state.showModal = isSaved;
        state.lastUpdated = moment().utc(true);
        state.invalidItems = [];
        state.promis = createEmptyPromisInventory(); // subsection does not need to be reset here because values are never entered into subsection
      }
    });
  }
});

export function createPromisRepo(patientId: string) {
  return createRepo<PromisState['promis']>(
    `${Collection.PatientAssessments}/${patientId}/${PatientSubcollection.InventoriesPromis}`
  );
}

export { categories, initialLoad, submit, tScoreCategories };

export const {
  setPromisDate,
  updatePromisItem,
  calculateCategories,
  calculateTScores,
  closeModal,
  validate
} = promisSlice.actions;
export const PromisSliceReducer = promisSlice.reducer;
export default promisSlice.reducer;
